import type { Biller } from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { FeatureFlags } from '@/components/feature-flags';
import { MoneyChannels } from '@/components/money-channels';
import { PageHeader } from '@/components/page-header';
import { ProposeChange } from '@/components/propose-change';
import { StatusBadge } from '@/components/record-table';
import { SettingsPanel } from '@/components/settings-panel';
import { TabLinks } from '@/components/tab-links';
import { Alert } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePager, usePaging } from '@/components/ui/table-pager';
import { adminQuery } from '@/lib/query';
import {
  filterSettings,
  isStaffVisibleKey,
  SETTING_TABS,
  type SettingTab,
  settingTabOf,
} from '@/lib/settings-meta';

const TAB_KEYS = [
  ...SETTING_TABS.map((item) => item.key),
  'flags',
  'bills',
] as const;

type Tab = (typeof TAB_KEYS)[number];

const searchSchema = z.object({
  tab: z.enum(TAB_KEYS).catch('channels').default('channels'),
});

export const Route = createFileRoute('/_app/settings')({
  validateSearch: searchSchema,
  component: SettingsPage,
});

/** Runtime settings by group, feature flags and the billers customers see. */
function SettingsPage() {
  const { tab } = Route.useSearch();
  const [query, setQuery] = useState('');
  const list = useQuery(adminQuery('settings.list', {}));
  const needle = query.trim();
  const counts = new Map<string, number>();
  if (needle && list.data) {
    for (const row of filterSettings(list.data, needle)) {
      if (!isStaffVisibleKey(row.key)) continue;
      const key = settingTabOf(row.key);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  const tabs = [
    ...SETTING_TABS.filter(
      (item) => item.key !== 'other' || list.data?.some(isOther) !== false,
    ),
    { key: 'flags', label: 'Feature flags' },
    { key: 'bills', label: 'Billers' },
  ].map((item) => ({
    ...item,
    label: counts.has(item.key)
      ? `${item.label} (${counts.get(item.key)})`
      : item.label,
  }));
  return (
    <>
      <PageHeader
        title="Settings"
        description="What the app and the server read while they run."
      />
      {tab === 'flags' || tab === 'bills' ? null : (
        <Input
          type="search"
          aria-label="Search settings"
          placeholder="Search settings"
          className="sm:max-w-sm"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}
      <TabLinks
        tabs={tabs}
        active={tab}
        render={(item, domProps) => (
          <Link
            {...domProps}
            to="/settings"
            search={{ tab: item.key as Tab }}
          />
        )}
      />
      {tab === 'flags' ? <FeatureFlags /> : null}
      {tab === 'bills' ? <Billers /> : null}
      {tab === 'channels' ? <MoneyChannels query={query} /> : null}
      {tab === 'flags' || tab === 'bills' || tab === 'channels' ? null : (
        <SettingsPanel tab={tab as SettingTab} query={query} />
      )}
    </>
  );
}

function isOther(row: { key: string }): boolean {
  return isStaffVisibleKey(row.key) && settingTabOf(row.key) === 'other';
}

// ---------------------------------------------------------------------------
// Billers
// ---------------------------------------------------------------------------

function Billers() {
  const list = useQuery(adminQuery('bills.catalog', {}));
  const paging = usePaging(list.data);
  if (list.isError) {
    return <Alert variant="destructive">{list.error.message}</Alert>;
  }
  return (
    <>
      <p className="text-sm text-muted-foreground">
        A hidden biller leaves the app's Pay bills list and refuses new
        payments. Payments already sent finish as usual.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Biller</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>In the app</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {paging.visible?.map((biller) => (
            <BillerRow key={biller.id} biller={biller} />
          ))}
        </TableBody>
      </Table>
      <TablePager paging={paging} />
    </>
  );
}

function BillerRow({ biller }: Readonly<{ biller: Biller }>) {
  const label = biller.hidden ? 'Show' : 'Hide';
  return (
    <TableRow>
      <TableCell>
        <div>{biller.name}</div>
        <div className="font-mono text-xs text-muted-foreground">
          {biller.id}
        </div>
      </TableCell>
      <TableCell className="capitalize">{biller.category}</TableCell>
      <TableCell>
        <StatusBadge status={biller.hidden ? 'hidden' : 'shown'} />
      </TableCell>
      <TableCell>
        <ProposeChange
          kind="biller_visibility"
          payload={{ biller_id: biller.id, hidden: !biller.hidden }}
          label={label}
          title={`${label} ${biller.name}`}
          description={
            biller.hidden
              ? 'Customers can pay this biller again.'
              : 'Customers stop seeing this biller.'
          }
        />
      </TableCell>
    </TableRow>
  );
}
